import React, {CSSProperties, MouseEvent, ReactNode, useState} from 'react'

import Colors from '@utils/Colors.js'

import Info from '../feedback/Info.js'
import {focusableClass, ensureFocusStyles} from '../styles.js'
import {buttonColors, buttonResetStyle, linkStyle} from './styles.js'

ensureFocusStyles()

interface LinkProps {
    children?: ReactNode
    style?: CSSProperties
    disabled?: boolean
    /** For a link without href: what clicking it does */
    onClick?: (event: MouseEvent) => void
    /** Opens this address in a new tab */
    href?: string
    /** Added to the <a>'s rel, which always has noopener noreferrer (the page opens in a new tab) */
    rel?: string
    /** Info icon after the link */
    info?: string
}

/**
 * Bold text that is clicked: an <a> that opens href in a new tab, or a <button> that calls onClick. It underlines on
 * hover. With info, an Info icon follows it and the style places both.
 */
export default function Link({children, style = {}, disabled, onClick, href, rel, info}: LinkProps) {
    const [hover, setHover] = useState(false)

    const defaultStyle: CSSProperties = {
        ...buttonResetStyle,
        ...linkStyle,
        color: disabled ? buttonColors.linkDisabled : buttonColors.link,
        wordBreak: 'break-word',
        cursor: disabled ? 'default' : 'pointer',
        textDecoration: hover && !disabled ? 'underline' : 'none',
        ...(!info && style)
    }

    const hoverProps = {
        onMouseEnter: () => setHover(true),
        onMouseLeave: () => setHover(false)
    }

    const link = href && !disabled ?
        // rel always has noopener noreferrer, the rule can't read that from the template
        // eslint-disable-next-line react/jsx-no-target-blank
        <a className={focusableClass} style={defaultStyle} href={href} target='_blank' rel={rel ? `noopener noreferrer ${rel}` : 'noopener noreferrer'} {...hoverProps}>
            {children}
        </a> :
        <button className={focusableClass} style={defaultStyle} type='button' disabled={disabled} onClick={onClick} {...hoverProps}>
            {children}
        </button>

    if (!info) {
        return link
    }

    return (
        <div style={{display: 'inline-flex', alignItems: 'baseline', ...style}}>
            {link}
            <Info style={{marginLeft: 6}} text={info} color={Colors.textMedium} />
        </div>
    )
}
